import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { CoreAppState } from '../../state/core-app.state';
import { ActiveThread, selectChat } from '../../state/chat/chat.reducer';
import {
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { chatActions } from '../../state/chat/chat.actions';
import { DateTime } from 'luxon';
import { MessageResourceTypeMappings } from '@aa/nest/resource';
import { selectAuth } from '../../state/auth/auth.reducer';
import { UserResourceTypeMappings } from '@aa/nest/auth';
import { getDateTimeFromOther } from 'src/libs/ts/common/src/lib/date-time/get-date-time-from-other';
import { EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

@Component({
  selector: 'aa-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    ChatMessageComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent {
  @HostBinding('class.in-modal') inModal: boolean = false;

  @Input() orderCardId?: number;
  @Input() customerProfileId?: number;

  @Output() close = new EventEmitter();

  @ViewChild('messages') messagesEl!: ElementRef;
  private readonly store = inject(Store<CoreAppState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  leftChat$ = new Subject<void>();

  currentUser$ = this.store
    .select((s) => selectAuth(s).user)
    .pipe(takeUntilDestroyed());
  isStaffApp$ = this.store
    .select((s) => s.isStaffApp)
    .pipe(takeUntilDestroyed());
  activeThread$!: Observable<ActiveThread | undefined>;

  messageFormControl = new FormControl('');

  ngOnDestroy() {
    const url = new URL(window.location.href);
    url.searchParams.delete('threadOrderCardId');
    url.searchParams.delete('threadCustomerProfileId');
    this.router.navigateByUrl(url.toString(), {
      onSameUrlNavigation: 'reload',
    });
    this.router.navigate([], {
      queryParams: {
        threadOrderCardId: null,
        threadCustomerProfileId: null,
      },
      queryParamsHandling: 'merge',
    });
    this.leftChat$.next();
    this.leftChat$.complete();
  }

  ngOnInit() {
    if (this.orderCardId) {
      this.activeThread$ = this.store
        .select((s) =>
          selectChat(s).activeThreads.find(
            (at) => at.thread.orderCardId == this.orderCardId,
          ),
        )
        .pipe(takeUntilDestroyed(this.destroyRef));
      this.store.dispatch(
        chatActions.tryConnectToThreadForOrderCard({
          orderCardId: this.orderCardId,
        }),
      );
    } else if (this.customerProfileId) {
      this.activeThread$ = this.store.select((s) =>
        selectChat(s).activeThreads.find(
          (at) => at.thread.customerProfileId == this.customerProfileId,
        ),
      );
      this.store.dispatch(
        chatActions.tryConnectToThreadForCustomer({
          customerId: this.customerProfileId,
        }),
      );
    } else {
      this.activeThread$ = this.store
        .select((s) => selectChat(s).activeThreads)
        .pipe(
          combineLatestWith(this.currentUser$),
          filter(([_, user]) => !!user?.customerProfileId),
          map(([activeThreads, user]) =>
            activeThreads.find(
              (at) => at.thread.customerProfileId === user?.customerProfileId,
            ),
          ),
        );

      this.currentUser$.subscribe((currentUser) => {
        if (currentUser?.customerProfileId) {
          this.store.dispatch(
            chatActions.tryConnectToThreadForCustomer({
              customerId: currentUser.customerProfileId,
            }),
          );
        }
      });
    }

    this.activeThread$
      .pipe(
        combineLatestWith(this.isStaffApp$),
        map(
          ([activeThread, isStaffApp]) =>
            activeThread?.messages.filter(
              (m) =>
                m.readAt == null &&
                (isStaffApp ? !!m.customerProfileId : m.staffProfileId),
            ),
        ),
        takeUntil(this.leftChat$),
      )
      .subscribe((messages) => {
        this.store.dispatch(
          chatActions.markMessagesAsRead({
            messageIds: messages?.map((m) => m.id) ?? [],
          }),
        );
      });

    this.activeThread$
      .pipe(
        map((at) => at?.messages.length),
        distinctUntilChanged(),
        debounceTime(50),
      )
      .subscribe((count) => {
        if (this.messagesEl) {
          const el = this.messagesEl?.nativeElement;

          el.scrollTo(0, el.scrollHeight);
        }
      });
  }

  async sendMessage() {
    const message = this.messageFormControl.value;
    if (!message || message.length <= 0) {
      return;
    }

    const activeThread = await firstValueFrom(this.activeThread$);
    if (!activeThread) {
      if (this.orderCardId) {
        this.store.dispatch(
          chatActions.createNewThread({
            orderCardId: this.orderCardId,
            initialMessage: {
              content: message,
              sentAt: DateTime.now().toJSDate(),
            },
          }),
        );
      } else {
        const customerProfileId =
          this.customerProfileId ??
          (await firstValueFrom(this.currentUser$))?.customerProfileId;

        if (!customerProfileId) {
          throw new Error(
            'Cannot create chat without a supplied orderCardId or customerProfileId',
          );
        }

        this.store.dispatch(
          chatActions.createNewThread({
            customerProfileId: customerProfileId,
            initialMessage: {
              content: message,
              sentAt: DateTime.now().toJSDate(),
            },
          }),
        );
      }
    } else {
      this.store.dispatch(
        chatActions.sendMessage({
          data: {
            content: message,
            sentAt: DateTime.now().toJSDate(),
            threadId: activeThread.thread.id,
          },
        }),
      );
    }

    this.messageFormControl.reset();
  }

  isTimeGap(
    messageA?: MessageResourceTypeMappings['resourceWithRelationsT'],
    messageB?: MessageResourceTypeMappings['resourceWithRelationsT'],
    minutes = 3,
  ) {
    return (
      messageA?.sentAt &&
      messageB?.sentAt &&
      getDateTimeFromOther(messageA.sentAt)!
        .diff(getDateTimeFromOther(messageB.sentAt)!)
        .as('minutes') > minutes
    );
  }

  getLastNonSenderMessage(
    messages: MessageResourceTypeMappings['resourceWithRelationsT'][],
    isStaffApp = false,
  ) {
    return messages
      .filter((m) => (isStaffApp ? !!m.customerProfileId : !!m.staffProfileId))
      .at(-1);
  }
  getLastReadSenderMessage(
    messages: MessageResourceTypeMappings['resourceWithRelationsT'][],
    isStaffApp = false,
  ) {
    return messages
      .filter(
        (m) =>
          !!m.readAt &&
          (isStaffApp ? !!m.staffProfileId : !!m.customerProfileId),
      )
      .at(-1);
  }

  isSender(
    message: MessageResourceTypeMappings['resourceWithRelationsT'],
    currentUser?: UserResourceTypeMappings['resourceWithRelationsT'],
    isStaffApp?: boolean,
  ) {
    if (!currentUser) return false;

    if (isStaffApp) {
      return currentUser.staffProfileId && message.staffProfileId;
    }

    return (
      currentUser.customerProfileId &&
      currentUser.customerProfileId == message.customerProfileId
    );
  }

  closeModal() {
    this.close.emit();
  }
}
